// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Tag from "@insite/mobius/Icons/Tag";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { wishListCardStyles } from "@turnerBlueprint/Overrides/Components/WishListCard";
import IconFavoriteEmpty from "@turnerBlueprint/XDL/Icons/IconFavoriteEmpty";
import IconFavoriteFilled from "@turnerBlueprint/XDL/Icons/IconFavoriteFilled";
import IconTrash from "@turnerBlueprint/XDL/Icons/IconTrash";
import IconViewEdit from "@turnerBlueprint/XDL/Icons/IconViewEdit";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

wishListCardStyles.gridContainer = {
    gap: 0,
    offsetCss: css`
        &&& {
            align-items: flex-start;

            ${xScreen("lte", "md")} {
                flex-direction: column;
                gap: 16px;
            }
        }
    `,
};

wishListCardStyles.basicIconGridContainer = {
    gap: 0,
    css: css`
        width: fit-content;
    `,
    offsetCss: css`
        gap: 24px;
        align-items: center;
    `,
};

wishListCardStyles.wishListInfoGridItem = {
    width: [12, 12, 12, 5, 5],
    align: "top",
};

wishListCardStyles.extendedInfoGridItem = {
    width: [12, 12, 12, 5, 5],
    css: css`
        align-items: baseline;
        ${xScreen("gte", "lg")} {
            min-height: 85px;
            align-items: center;
        }
    `,
};

wishListCardStyles.basicInfoGridItem = {
    width: 12,
    css: css`
        align-items: center;
    `,
};

wishListCardStyles.detailLinkGridItem = {
    width: 12,
    css: css`
        & > ${StyledButton} {
            font-weight: bold;

            ${TypographyStyle} {
                font-size: 16px;
            }
        }
    `,
};

wishListCardStyles.basicIconGridItem = {
    css: css`
        align-items: center;
        font-size: 12px;
        line-height: 13px;
        gap: 20px;
        ${xScreen("lte", "md")} {
            justify-content: flex-start;
            &:empty {
                display: none;
            }
        }
    `,
};

wishListCardStyles.actionWrapper = {
    css: css`
        width: auto;
        display: flex;
        flex-direction: column;
        gap: 16px;
    `,
};

wishListCardStyles.productImage = {
    width: "91px",
    height: "85px",
    css: css`
        object-fit: contain;
        img {
            height: auto;
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
        }
    `,
    errorTypographyProps: {
        size: 11,
        css: css`
            max-height: 65px;
        `,
    },
};

wishListCardStyles.productImagesGridItem = {
    width: [12],
    css: css`
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        gap: 22px;

        ${xScreen("lte", "md")} {
            gap: 20px;
            flex-direction: column;
            align-items: flex-start;

            > div:empty + span {
                gap: 0;
            }
            > div:empty {
                display: none;
            }
        }
    `,
};

wishListCardStyles.lastUpdatedText = {
    size: 12,
    lineHeight: "13px",
};

wishListCardStyles.imageWrapper = {
    css: css`
        min-width: 317px;
        display: flex;
        gap: 22px;
    `,
};

wishListCardStyles.actionAndStatusGridItem = {
    width: [12, 12, 12, 2, 2],
    css: css`
        justify-content: flex-start;
        align-items: center;
        ${xScreen("lte", "sm")} {
            align-items: flex-start;
        }
    `,
};

wishListCardStyles.sharingStatus = {
    statusText: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 20px;
            span {
                margin-left: 0;
            }
        `,
    },
    privateTooltip: {
        iconProps: {
            css: css`
                &&&& {
                    ${xScreen("lte", "md")} {
                        margin-left: 0;
                    }
                }
            `,
        },
    },
};

wishListCardStyles.productImageLink = {
    css: css`
        margin: 0;
    `,
    typographyProps: {
        css: css`
            display: flex;
        `,
    },
};

wishListCardStyles.actionAddToCartLink = {
    ...createLinkIconStyles(
        "ShoppingCart",
        {},
        {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
    ),
};

wishListCardStyles.actionDeleteLink = {
    ...createLinkIconStyles(
        IconTrash,
        {},
        {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
    ),
};

wishListCardStyles.actionViewEditLink = {
    ...createLinkIconStyles(
        IconViewEdit,
        {},
        {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
    ),
};
wishListCardStyles.actionHideLink = {
    ...createLinkIconStyles(
        "EyeOff",
        {},
        {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
    ),
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        ${IconWrapper} {
            color: ${color("common.ui4")};
            opacity: 0.66;
        }
    `,
};

wishListCardStyles.descriptionText = {
    size: 12,
    lineHeight: "14px",
    css: css`
        ${ellipsis(2)}
    `,
};

wishListCardStyles.wishListInfoGridContainer = { gap: 0 };

wishListCardStyles.basicInfoGridContainer = {
    gap: 0,
    offsetCss: css`
        gap: 8px;
    `,
};
wishListCardStyles.lastUpdateAndIconGridItem = { width: 12 };
wishListCardStyles.lastUpdateAndIconGridContainer = {
    gap: 0,
    offsetCss: css`
        align-items: center;
        gap: 4px;
    `,
};
wishListCardStyles.lastUpdatedGridItem = {
    width: 12,
    css: css`
        max-width: fit-content;
    `,
};
wishListCardStyles.extendedInfoGridContainer = { gap: 0 };
wishListCardStyles.actionAndStatusGridContainer = {
    gap: 0,
    offsetCss: css`
        ${xScreen("lte", "md")} {
            gap: 16px;
        }
    `,
};
wishListCardStyles.statusGridItem = {
    width: [12, 12, 12, 4, 4],
    css: css`
        align-items: center;
    `,
};

wishListCardStyles.actionGridItem = { width: [12] };

wishListCardStyles.starIcon = {
    src: IconFavoriteEmpty,
    size: 14,
    css: css`
        &&& {
            color: ${color("text.link")};
            svg {
                margin-bottom: 2px;
            }
        }
    `,
};
wishListCardStyles.starXIcon = {
    src: IconFavoriteFilled,
    size: 14,
    css: css`
        &&& {
            color: ${color("text.link")};
            &,
            svg {
                height: auto;
                margin-bottom: 2px;
            }
        }
    `,
};
wishListCardStyles.actionLeaveLink = {
    typographyProps: {
        size: 12,
        css: css`
            line-height: 13px;
        `,
    },
};

wishListCardStyles.tagTooltip = {
    iconProps: {
        src: Tag,
        size: 15,
        css: css`
            margin: 4px 0;
        `,
    },
    typographyProps: {
        css: css`
            overflow: auto;
            max-height: 200px;
            padding-right: 8px;
            word-break: break-word;
        `,
    },
};
